import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MessageSquare, Loader2, Ticket, Mail, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState(searchParams.get('code') || '');
  const [isLoading, setIsLoading] = useState(false);
  const [verificationPending, setVerificationPending] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const code = inviteCode.trim() || null;
    const result = await register(email, password, name, code);
    setIsLoading(false);

    if (result.verification_pending) {
      setVerificationPending(true);
      setPendingEmail(result.email || email);
      toast.success('Revisa tu email para verificar tu cuenta');
    } else if (result.success) {
      if (code) {
        toast.success('Cuenta creada con acceso beta activado!');
        navigate('/dashboard');
      } else if (result.requires_checkout) {
        toast.success('Cuenta creada! Ahora elige tu plan para comenzar tu prueba gratis.');
        navigate('/plans');
      } else {
        toast.success('Cuenta creada exitosamente!');
        navigate('/dashboard');
      }
    } else {
      toast.error(result.error || 'Error al registrar');
    }
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/resend-verification`, { email: pendingEmail });
      toast.success('Email de verificacion reenviado');
    } catch {
      toast.error('Error al reenviar. Intenta de nuevo.');
    }
    setResending(false);
  };

  const handleGoogleSignup = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (verificationPending) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center" data-testid="verification-pending-screen">
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-indigo-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-950 mb-3">Verifica tu email</h2>
          <p className="text-gray-600 mb-2">
            Enviamos un enlace de verificacion a:
          </p>
          <p className="text-indigo-600 font-semibold mb-6">{pendingEmail}</p>
          <p className="text-sm text-gray-500 mb-8">
            Haz clic en el enlace del email para activar tu cuenta. Revisa tu carpeta de spam si no lo encuentras.
          </p>
          <Button
            onClick={handleResendVerification}
            disabled={resending}
            variant="outline"
            className="border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg px-6 py-2.5"
            data-testid="resend-verification-btn"
          >
            {resending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Reenviar email
          </Button>
          <div className="mt-6">
            <Link to="/login" className="text-sm text-indigo-600 hover:text-indigo-700">
              Ya verifique mi email, iniciar sesion
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-purple-600 to-indigo-600 items-center justify-center p-12">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="relative z-10 text-white max-w-lg">
          <MessageSquare className="w-16 h-16 mb-6" />
          <h1 className="text-4xl font-bold mb-4 tracking-tight">Comienza a Construir tu Bot Hoy</h1>
          <p className="text-lg leading-relaxed opacity-90">
            Crea asistentes profesionales de WhatsApp en minutos. No se requiere programacion. Unete a miles de usuarios satisfechos.
          </p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <Link to="/" className="flex items-center gap-2 mb-8">
              <MessageSquare className="w-7 h-7 text-indigo-600" />
              <span className="text-xl font-bold tracking-tight text-gray-950">ACBotHub</span>
            </Link>
            <h2 className="text-3xl font-bold tracking-tight text-gray-950 mb-2">Crea tu cuenta</h2>
            <p className="text-gray-600">Comienza con tu prueba gratis de 7 dias</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div>
              <Label htmlFor="name" className="text-gray-700 font-medium">Nombre completo</Label>
              <Input
                id="name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="register-name-input"
                className="mt-2 bg-white border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                placeholder="Juan Perez"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-gray-700 font-medium">Correo electronico</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="register-email-input"
                className="mt-2 bg-white border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                placeholder="tu@ejemplo.com"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-gray-700 font-medium">Contrasena</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="register-password-input"
                className="mt-2 bg-white border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                placeholder="********"
              />
              <p className="mt-1 text-sm text-gray-500">Minimo 6 caracteres</p>
            </div>

            <div>
              <Label htmlFor="inviteCode" className="text-gray-700 font-medium flex items-center gap-2">
                <Ticket className="w-4 h-4 text-indigo-500" />
                Codigo de Invitacion
                <span className="text-xs text-gray-400 font-normal">(opcional)</span>
              </Label>
              <Input
                id="inviteCode"
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                data-testid="register-invite-code-input"
                className="mt-2 bg-white border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 uppercase tracking-wider"
                placeholder="BETA-XXXXXX"
                maxLength={11}
              />
              {inviteCode && (
                <p className="mt-1 text-xs text-indigo-600">Con codigo beta no necesitas tarjeta de credito</p>
              )}
            </div>

            <div className="flex items-start gap-2.5">
              <input
                type="checkbox"
                id="acceptTerms"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                data-testid="accept-terms-checkbox"
                className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
              />
              <label htmlFor="acceptTerms" className="text-sm text-gray-600 leading-snug cursor-pointer">
                He leido y acepto los{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 font-medium underline">Terminos y Condiciones</a>
                {' '}y la{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 font-medium underline">Politica de Privacidad</a>
              </label>
            </div>

            <Button
              type="submit"
              disabled={isLoading || !acceptTerms}
              data-testid="register-submit-btn"
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md hover:shadow-lg transition-all rounded-lg font-medium px-6 py-2.5"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creando cuenta...
                </>
              ) : (
                'Crear cuenta'
              )}
            </Button>
          </form>

          {!inviteCode && (
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">O continua con</span>
                </div>
              </div>

              <Button
                type="button"
                onClick={handleGoogleSignup}
                data-testid="google-signup-btn"
                className="mt-6 w-full bg-white border border-gray-200 text-gray-900 hover:bg-gray-50 transition-all rounded-lg font-medium px-6 py-2.5 flex items-center justify-center gap-3"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continuar con Google
              </Button>
            </div>
          )}

          <p className="mt-8 text-center text-gray-600">
            Ya tienes una cuenta?{' '}
            <Link to="/login" className="text-indigo-600 hover:text-indigo-700 font-medium">
              Inicia sesion
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
